import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a top sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (window.scrollY > 120 && rect.bottom <= 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles variant selection change from the sticky bar dropdown (two-way sync with main variant-picker)
   * @param {Event} event
   */
  handleVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement | null} */ (event.target);
    if (!select) return;

    const selectedOption = select.selectedOptions?.[0];
    const variantId = select.value;
    if (!variantId || !selectedOption) return;

    this.dataset.currentVariantId = variantId;

    // Immediately update sticky bar price & image for instant visual feedback
    const stickyPriceEl = this.refs.stickyBar?.querySelector('.sticky-add-to-cart__price .price');
    if (stickyPriceEl && selectedOption.dataset.price) {
      stickyPriceEl.textContent = selectedOption.dataset.price;
    }
    if (this.refs.productImage && selectedOption.dataset.image) {
      this.refs.productImage.src = selectedOption.dataset.image;
    }

    // Update hidden variant ID input on the main product form immediately
    const productForm = this.#getProductForm();
    const idInput = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="id"]'));
    if (idInput) {
      idInput.value = variantId;
    }

    // Sync option1 / option2 / option3 back to the main <variant-picker>
    const productId = this.dataset.productId;
    const variantPicker = /** @type {any} */ (
      document.querySelector(`variant-picker[data-product-id="${productId}"]`) ||
        this.closest('.shopify-section')?.querySelector('variant-picker')
    );
    if (!variantPicker) return;

    const desiredOptions = [
      selectedOption.dataset.option1,
      selectedOption.dataset.option2,
      selectedOption.dataset.option3,
    ].filter((val) => val !== undefined && val !== '');

    let lastChangedControl = /** @type {HTMLElement | null} */ (null);
    const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));

    if (fieldsets.length > 0) {
      fieldsets.forEach((fieldset, idx) => {
        const desiredVal = desiredOptions[idx];
        if (!desiredVal) return;
        const radios = /** @type {HTMLInputElement[]} */ (Array.from(fieldset.querySelectorAll('input[type="radio"]')));
        const matchingRadio = radios.find((r) => r.value === desiredVal);
        if (matchingRadio) {
          if (!matchingRadio.checked) {
            if (typeof variantPicker.updateSelectedOption === 'function') {
              variantPicker.updateSelectedOption(matchingRadio);
            } else {
              matchingRadio.checked = true;
            }
          }
          lastChangedControl = matchingRadio;
        }
      });
    } else {
      const selects = /** @type {HTMLSelectElement[]} */ (
        Array.from(variantPicker.querySelectorAll('select.variant-option__select, select'))
      );
      selects.forEach((s, idx) => {
        const desiredVal = desiredOptions[idx];
        if (!desiredVal) return;
        if (s.value !== desiredVal) {
          s.value = desiredVal;
          if (typeof variantPicker.updateSelectedOption === 'function') {
            variantPicker.updateSelectedOption(s);
          }
        }
        lastChangedControl = s;
      });
    }

    if (lastChangedControl) {
      lastChangedControl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Handles the add to cart button click in the sticky bar.
   * Respects personalization validation ([data-pz-root]) and form validity before playing any animation.
   */
  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();
    if (!this.#targetAddToCartButton && productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
    if (!this.#targetAddToCartButton) return;

    // Track whether the click event propagated past capture-phase validators (e.g. sanctaria-personalize)
    let clickPropagated = false;
    const onTargetClick = () => {
      clickPropagated = true;
    };
    this.#targetAddToCartButton.addEventListener('click', onTargetClick, { once: true });

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    this.#targetAddToCartButton.removeEventListener('click', onTargetClick);

    // Check if personalization block or native form validation blocked submission
    const sectionEl = this.closest('.shopify-section') || document;
    const pzRoot = sectionEl.querySelector('[data-pz-root]') || document.querySelector('[data-pz-root]');
    const pzInvalidEl = pzRoot
      ? pzRoot.querySelector(
          '.is-invalid, [aria-invalid="true"], input:invalid, select:invalid, textarea:invalid'
        )
      : null;

    const formElement = /** @type {HTMLFormElement | null} */ (productForm?.querySelector('form') ?? null);
    const formInvalid = formElement ? !formElement.checkValidity() : false;

    if (!clickPropagated || pzInvalidEl || formInvalid) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
      const scrollTarget =
        pzInvalidEl ||
        (formElement ? formElement.querySelector(':invalid') : null) ||
        pzRoot;
      if (scrollTarget instanceof HTMLElement) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated on the main variant-picker)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId, variantId } = event.detail ?? {};
    if (variantId) {
      this.dataset.currentVariantId = String(variantId);
    } else if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Explicitly sync sticky variant <select> value and price HTML after morph
        const stickySelect = /** @type {HTMLSelectElement | null} */ (
          this.refs.stickyBar.querySelector('.sticky-add-to-cart__variant-select')
        );
        const resolvedVariantId = variant?.id ? String(variant.id) : newStickyAddToCart.dataset.currentVariantId;
        if (stickySelect && resolvedVariantId) {
          stickySelect.value = String(resolvedVariantId);
          for (const opt of stickySelect.options) {
            opt.toggleAttribute('selected', opt.value === String(resolvedVariantId));
          }
        }

        const newPriceEl = newStickyBar.querySelector('.sticky-add-to-cart__price');
        const curPriceEl = this.refs.stickyBar.querySelector('.sticky-add-to-cart__price');
        if (newPriceEl && curPriceEl) {
          curPriceEl.innerHTML = newPriceEl.innerHTML;
        }

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (resolvedVariantId) {
          this.dataset.currentVariantId = String(resolvedVariantId);
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Top sticky bar does not overlap bottom-right Shopify Inbox chat button.
   * Always return false so <shopify-chat> never suppresses the sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    return false;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
